import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import DoxAmbient from "@/components/DoxAmbient";
import WorkHistorySection from "@/components/WorkHistorySection";
import { useDoxPreview } from "@/context/DoxPreviewContext";
import { useDoxWork } from "@/context/DoxWorkContext";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * The Zen Overview.
 *
 * Answers one question: "Am I okay?"
 *
 * If yes  → "You're covered. Go run your business. I'll keep watching."
 * If not  → "I found one thing." + ONE action in Dox's voice.
 *
 * Nothing else on the front door. Substance lives one tap away in
 * "Show me what I've been up to" (Dox's Work History) or via the sidebar.
 *
 * No grids. No stat cards. No priority lists. No dashboards.
 */
export default function ZenOverview({ onNav }) {
  const [status, setStatus] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const { openDoxPreview } = useDoxPreview();
  const { runDoxWork } = useDoxWork();

  const refresh = () => api.get("/overview/status")
    .then((r) => setStatus(r.data))
    .catch(() => setStatus({ state: "clear", headline: "You're covered.", subline: "I'll keep watching. Go run your business.", action: null }));

  useEffect(() => { refresh(); }, []);

  const clear = status?.state !== "one_thing";

  const primaryAction = () => {
    const a = status?.action;
    if (!a) return;

    // Preview path — pre-trial users watch Dox work before trusting him.
    if (a.preferred_action === "preview") {
      openDoxPreview?.();
      return;
    }

    // Live path — Dox performs the work. For approve, we bypass the doc
    // drawer and fire the real backend call inside the Performance.
    if (a.preferred_action === "live" && a.kind === "approve" && a.target_id) {
      runDoxWork?.({
        ackLine: "I've got it.",
        deliverLine: "Filed.",
        stages: [
          { label: "Reviewing your document", emoji: "📄" },
          { label: "Verifying against state records", emoji: "✅" },
          { label: "Checking compliance", emoji: "🛡️" },
          { label: "Filing on your behalf", emoji: "✍️" },
        ],
        run: () => api.post(`/documents/${a.target_id}/approve`),
        onResolved: () => { toast.success("Dox handled it."); refresh(); },
        onError: () => toast.error("Couldn't file that one."),
      });
      return;
    }

    // Everything else — just navigate to the right sidebar tab.
    if (a.nav) onNav?.(a.nav);
  };

  // The promise-first three-line stanza. Clear state = "You're covered. /
  // I'll keep watching. / Go run your business." One-thing state = the
  // headline + Dox-voice subline as separate lines, preserving the same
  // typographic rhythm.
  const stanza = status
    ? clear
      ? [
          { mark: "check", text: "You're covered." },
          { text: "I'll keep watching." },
          { text: "Go run your business." },
        ]
      : [
          { mark: "attention", text: status.headline },
          { text: status.subline },
        ]
    : [];

  return (
    <div className="min-h-[calc(100vh-120px)] flex flex-col" data-testid="zen-overview">
      {/* The one moment */}
      <div className="flex flex-col items-center justify-center text-center pt-16 sm:pt-24 pb-20 flex-1">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.33, 1, 0.68, 1] }}
        >
          <DoxAmbient size={130} />
        </motion.div>

        <AnimatePresence mode="wait">
          {status && (
            <motion.div
              key={status.state}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.25, ease: [0.33, 1, 0.68, 1] }}
              className="mt-12 max-w-xl mx-auto"
            >
              <div className="space-y-4" data-testid="zen-headline">
                {stanza.map((line, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: 0.35 + i * 0.18, ease: [0.33, 1, 0.68, 1] }}
                    className="flex items-center justify-center gap-3"
                  >
                    {line.mark === "check" && <ZenCheck />}
                    {line.mark === "attention" && <ZenAttention />}
                    <span
                      className={`font-display font-black text-white tracking-[-0.02em] leading-[1.02] ${
                        i === 0
                          ? "text-4xl sm:text-5xl lg:text-6xl"
                          : "text-2xl sm:text-3xl lg:text-4xl text-white/70 font-medium"
                      }`}
                      data-testid={i === 1 && !clear ? "zen-subline" : undefined}
                    >
                      {line.text}
                    </span>
                  </motion.div>
                ))}
              </div>

              {status.action && (
                <motion.button
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.9, ease: [0.33, 1, 0.68, 1] }}
                  data-testid="zen-action"
                  onClick={primaryAction}
                  className="mt-12 rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-semibold text-base px-9 py-4 min-h-[52px] shadow-[0_10px_40px_rgba(30,120,255,0.35)] transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  {status.action.label}
                </motion.button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Substance on demand — Dox's Work History collapses below the fold */}
      <div className="pb-4">
        <button
          data-testid="zen-show-more"
          onClick={() => setRevealed((v) => !v)}
          className="mx-auto flex items-center gap-2 text-white/45 hover:text-white/80 text-sm tracking-wide transition-colors"
        >
          <span>{revealed ? "Hide" : "Show me what I've been up to"}</span>
          <ChevronDown
            size={14}
            className={`transition-transform ${revealed ? "rotate-180" : ""}`}
          />
        </button>

        <AnimatePresence initial={false}>
          {revealed && (
            <motion.div
              key="wh"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
              className="overflow-hidden"
            >
              <div className="pt-10">
                <WorkHistorySection variant="embedded" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tiny status marks — no icons, just quiet symbols                            */
/* -------------------------------------------------------------------------- */

function ZenCheck() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center h-11 w-11 sm:h-14 sm:w-14 rounded-full bg-emerald-500/15 border border-emerald-400/25"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

function ZenAttention() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center h-11 w-11 sm:h-14 sm:w-14 rounded-full bg-amber-500/15 border border-amber-400/25"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-amber-300">
        <line x1="12" y1="8" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12" y2="17.01" />
      </svg>
    </span>
  );
}
